import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Task } from '@/types/tasks';
import { cn } from '@/lib/utils';
import EditTaskModal from '../EditTaskModal';
import useTaskCardState from './hooks/useTaskCardState';
import useTaskCardDrag from './hooks/useTaskCardDrag';
import TaskCardContainer from './TaskCardContainer';
import TaskCardContent from './TaskCardContent';

interface TaskCardProps {
  task: Task;
  onUpdateTask?: (task: Task) => void;
  onDeleteTask?: (taskId: string) => void;
  isDragOverlay?: boolean;
  hideGoalName?: boolean;
  // NEW: Context-aware props following New Task system pattern
  context?: 'task-manager' | 'project';
  showAssignees?: boolean;
  // Existing props
  customTaskOperations?: {
    completeTask?: (taskId: string) => void;
    uncompleteTask?: (taskId: string) => void;
    removeTask?: (taskId: string) => void;
    updateTask?: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
  };
  onGenerateContent?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  onGenerateResearch?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  onGenerateImage?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  isGeneratingContent?: boolean;
  workbookId?: string; // For navigation to generated content
}

const TaskCard = ({ 
  task, 
  onUpdateTask, 
  isDragOverlay = false, 
  hideGoalName,
  context, // NEW: Optional explicit context
  showAssignees, // NEW: Optional explicit assignee control
  customTaskOperations,
  onGenerateContent,
  onGenerateResearch,
  onGenerateImage,
  isGeneratingContent,
  workbookId
}: TaskCardProps) => {
  const location = useLocation();
  
  // Smart context inference for backward compatibility
  const inferredContext = location.pathname.includes('/project/') ? 'project' : 'task-manager';
  const finalContext = context || inferredContext;
  
  // Smart assignee visibility logic
  const finalShowAssignees = showAssignees !== undefined 
    ? showAssignees 
    : finalContext === 'project'; // Only show assignees in project context
  
  // Use explicit prop if provided, otherwise auto-detect for goal name
  const shouldHideGoalName = hideGoalName !== undefined ? hideGoalName : finalContext === 'project';

  const {
    isCompleted,
    isEditModalOpen,
    setIsEditModalOpen,
    isDeleting,
    handleCheckboxChange,
    handleCardClick, // This is now used to open the edit modal
    handleClickStopPropagation,
    handleEditTask,
    handleUpdateTask,
    handleDeleteTask
  } = useTaskCardState({ 
    task, 
    onUpdateTask,
    customTaskOperations 
  });
  
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    style,
    isDragging,
  } = useTaskCardDrag({ task });

  // Listen for custom events to open this task's edit modal
  useEffect(() => {
    const handleOpenTaskEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const taskId = customEvent.detail?.taskId;
      
      if (taskId === task.id) {
        console.log('[TaskCard] Opening edit modal for task:', taskId);
        setIsEditModalOpen(true);
      }
    };

    window.addEventListener('open-task-detail', handleOpenTaskEvent);
    
    return () => {
      window.removeEventListener('open-task-detail', handleOpenTaskEvent);
    };
  }, [task.id, setIsEditModalOpen]);

  return (
    <>
      <div
        ref={isDragOverlay ? undefined : setNodeRef}
        style={isDragOverlay ? undefined : style}
        className={cn(
          "relative mb-2",
          isDragging ? "z-50" : "z-10"
        )}
        onClick={isDragOverlay ? undefined : handleCardClick}
        tabIndex={isDragOverlay ? undefined : 0}
      >
        <TaskCardContainer isDeleting={isDeleting} isDragging={isDragging}>
          <TaskCardContent 
            task={task}
            isCompleted={isCompleted}
            handleCheckboxChange={handleCheckboxChange}
            handleClickStopPropagation={handleClickStopPropagation}
            onCardClick={(e) => {
              e.stopPropagation();
              setIsEditModalOpen(true);
            }}
            dragHandleRef={isDragOverlay ? undefined : setActivatorNodeRef}
            dragListeners={isDragOverlay ? undefined : listeners}
            dragAttributes={isDragOverlay ? undefined : attributes}
            hideGoalName={shouldHideGoalName}
            onGenerateContent={onGenerateContent}
            onGenerateResearch={onGenerateResearch}
            onGenerateImage={onGenerateImage}
            isGeneratingContent={isGeneratingContent}
            workbookId={workbookId}
          />
        </TaskCardContainer>
      </div>

      {/* Edit Task Modal - Now context-aware following New Task pattern */}
      {!isDragOverlay && (
        <EditTaskModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onUpdateTask={handleUpdateTask}
          onDeleteTask={handleDeleteTask}
          task={task}
          context={finalContext}
          showAssignees={finalShowAssignees}
        />
      )}
    </>
  );
};

export default TaskCard;
